import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { GestionService } from '../../core/services/gestion.service';
import { TiposDocumentoService } from '../../core/services/tipos-documento.service';
import { NotificationService } from '../../services/notification.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { CustomSelectComponent } from '../shared/custom-select/custom-select.component';
import { calcularDigitoVerificacionNIT } from '../../core/utils/nit.util';
import {
  EstadoTramiteSolicitud,
  comentarioEstadoTramiteHtml,
  tieneComentariosEstadoTramite
} from '../../core/utils/estado-tramite.util';
import {
  TipoDocumentoOption,
  abreviaturaCanon,
  abreviaturaParaApiPostventa,
  esAbreviaturaDocumentoNit,
  mensajeNumeroDocumentoInvalido,
  numeroDocumentoInvalido,
  sanitizarNumeroDocumento
} from '../../core/utils/tipos-documento.util';

@Component({
  selector: 'app-gestion-estado-tramite',
  templateUrl: './gestion-estado-tramite.component.html',
  styleUrls: ['./gestion-estado-tramite.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CustomSelectComponent]
})
export class GestionEstadoTramiteComponent implements OnInit, OnDestroy {
  tiposDocumentos: TipoDocumentoOption[] = [];

  formaForm: FormGroup;
  listadoSolicitudes: EstadoTramiteSolicitud[] = [];
  tituloConsulta = '';
  /** Saludo corto tras identificar al titular (mismo criterio que descarga). */
  nombreSaludo = '';
  filtroLocal = '';
  solicitudExpandidaId: number | string | null = null;
  loading = false;
  errorMessage = '';
  comas = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private gestion: GestionService,
    private tiposDocumentoService: TiposDocumentoService,
    private uiHelper: UiHelperService,
    private notification: NotificationService
  ) {
    this.formaForm = this.fb.group(
      {
        tipo: ['CC', [Validators.required]],
        numero: ['', [Validators.required]],
        codigo: [{ value: '', disabled: true }]
      },
      { validators: [this.validadorDocumento()] }
    );
  }

  ngOnInit(): void {
    this.tiposDocumentoService
      .opcionesSelect$('todos')
      .pipe(takeUntil(this.destroy$))
      .subscribe((opts) => {
        this.tiposDocumentos = opts;
        const actual = abreviaturaCanon(this.formaForm.get('tipo')?.value);
        if (actual && !opts.some((o) => o.value === actual) && opts.length) {
          this.formaForm.get('tipo')!.setValue(opts[0].value === 'NIT' ? 'CC' : opts[0].value);
        }
      });

    this.formaForm
      .get('tipo')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onTipoChange());

    this.formaForm
      .get('numero')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.onNumeroChange(String(val ?? '')));

    this.onTipoChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.uiHelper.hideSpinner();
  }

  get tieneResultados(): boolean {
    return this.listadoSolicitudes.length > 0;
  }

  get listadoFiltrado(): EstadoTramiteSolicitud[] {
    const q = this.normalizarTexto(this.filtroLocal);
    if (!q) {
      return this.listadoSolicitudes;
    }
    return this.listadoSolicitudes.filter((item) => this.coincideFiltro(item, q));
  }

  get hayFiltroActivo(): boolean {
    return this.filtroLocal.trim().length > 0;
  }

  /** Mismo titular en todas las filas: se oculta en la lista (ya está en el saludo). */
  get titularCompartido(): boolean {
    return this.campoCompartidoEnListado((item) => item.nombreTitular);
  }

  /** Mismo mecanismo en todas las filas: chip más suave (menos ruido visual). */
  get mecanismoCompartido(): boolean {
    return this.campoCompartidoEnListado((item) => item.mecanismo);
  }

  get esNit(): boolean {
    return esAbreviaturaDocumentoNit(this.formaForm.get('tipo')?.value);
  }

  get mensajeNumero(): string {
    return mensajeNumeroDocumentoInvalido(this.formaForm.get('tipo')?.value);
  }

  camposInvalidos(campo: string): boolean {
    const ctrl = this.formaForm.get(campo);
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  onNumeroKeyup(event: KeyboardEvent): void {
    const key = event.key;
    this.comas = key === '.' || key === ',' || key === 'Decimal';
  }

  consultar(): void {
    if (this.formaForm.invalid) {
      this.formaForm.markAllAsTouched();
      return;
    }
    this.errorMessage = '';
    this.solicitudExpandidaId = null;
    this.setLoading(true);
    this.uiHelper.showSpinner(
      'Estamos cargando tus solicitudes…',
      'gestion',
      'Estado del trámite'
    );

    const raw = this.formaForm.getRawValue();
    const tipoUi = abreviaturaCanon(raw.tipo);
    const tipo = abreviaturaParaApiPostventa(tipoUi);
    const numero = String(raw.numero ?? '').trim();
    const codigo = String(raw.codigo ?? '').trim();
    const identificacion = this.esNit ? `${numero}${codigo}` : numero;

    this.gestion
      .consultarSolicitudesEstadoTramite({ tipo, identificacion })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (listado) => {
          this.setLoading(false);
          this.uiHelper.hideSpinner();
          this.listadoSolicitudes = listado;
          this.filtroLocal = '';
          this.solicitudExpandidaId =
            listado.length === 1 ? (listado[0].solicitudId ?? null) : null;

          if (!listado.length) {
            this.tituloConsulta = '';
            this.nombreSaludo = '';
            this.errorMessage = `No se encontraron trámites asociados a la identificación ${identificacion}.`;
            return;
          }

          const docLabel =
            this.tiposDocumentos.find((t) => t.value === tipoUi)?.label || tipoUi;
          const dv = this.esNit && codigo ? `-${codigo}` : '';
          this.nombreSaludo = this.armarNombreSaludo(listado[0].nombreTitular);
          this.tituloConsulta = `${docLabel}: ${numero}${dv}`;
        },
        error: (error) => {
          const msg = String(error?.error?.message ?? error?.message ?? '').toLowerCase();
          if (msg.includes('timed out')) {
            this.consultar();
            return;
          }
          this.setLoading(false);
          this.uiHelper.hideSpinner();
          this.errorMessage = this.extraerMensajeError(error);
        }
      });
  }

  toggleSolicitud(item: EstadoTramiteSolicitud): void {
    const id = item.solicitudId ?? null;
    this.solicitudExpandidaId = this.solicitudExpandidaId === id ? null : id;
  }

  estaExpandida(item: EstadoTramiteSolicitud): boolean {
    return this.solicitudExpandidaId === (item.solicitudId ?? null);
  }

  nuevaConsulta(): void {
    this.formaForm.reset({ tipo: 'CC', numero: '', codigo: '' });
    this.onTipoChange();
    this.listadoSolicitudes = [];
    this.tituloConsulta = '';
    this.nombreSaludo = '';
    this.filtroLocal = '';
    this.solicitudExpandidaId = null;
    this.errorMessage = '';
    this.comas = false;
    this.setLoading(false);
  }

  onFiltroInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.filtroLocal = value;
    if (this.solicitudExpandidaId == null) {
      return;
    }
    const visible = this.listadoFiltrado.some(
      (item) => (item.solicitudId ?? null) === this.solicitudExpandidaId
    );
    if (!visible) {
      this.solicitudExpandidaId = null;
    }
  }

  limpiarFiltro(): void {
    this.filtroLocal = '';
  }

  esPasoActual(item: EstadoTramiteSolicitud, index: number): boolean {
    const pasos = item.pasos ?? [];
    const paso = pasos[index];
    if (!paso || paso.finalizado) {
      return false;
    }
    return index === 0 || !!pasos[index - 1]?.finalizado;
  }

  pasosTotal(item: EstadoTramiteSolicitud): number {
    return item.pasos?.length ?? 0;
  }

  pasosHechos(item: EstadoTramiteSolicitud): number {
    return (item.pasos ?? []).filter((p) => p.finalizado).length;
  }

  textoProgreso(item: EstadoTramiteSolicitud): string {
    return `${this.pasosHechos(item)} de ${this.pasosTotal(item)} pasos`;
  }

  tieneComentarios(paso: { comentarios?: string }): boolean {
    return tieneComentariosEstadoTramite(paso?.comentarios);
  }

  mostrarComentarios(comentarios: unknown): void {
    const html = comentarioEstadoTramiteHtml(comentarios);
    if (!html.trim()) {
      return;
    }
    void Swal.fire({
      ...this.notification.getSwalBaseConfig(),
      icon: 'info',
      title: 'Detalle del paso',
      html,
      confirmButtonText: 'Entendido',
      customClass: this.notification.swalCustomClass
    });
  }

  private onTipoChange(): void {
    const codigo = this.formaForm.get('codigo')!;
    const numero = String(this.formaForm.get('numero')?.value ?? '');
    this.onNumeroChange(numero);
    if (this.esNit) {
      codigo.enable({ emitEvent: false });
      codigo.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]$/)
      ]);
    } else {
      codigo.clearValidators();
      codigo.setValue('', { emitEvent: false });
      codigo.disable({ emitEvent: false });
    }
    codigo.updateValueAndValidity({ emitEvent: false });
    this.formaForm.updateValueAndValidity({ emitEvent: false });
  }

  private onNumeroChange(raw: string): void {
    const tipo = this.formaForm.get('tipo')?.value;
    const limpio = sanitizarNumeroDocumento(tipo, raw);
    const numeroCtrl = this.formaForm.get('numero')!;
    if (limpio !== raw) {
      numeroCtrl.setValue(limpio, { emitEvent: false });
    }
    if (this.esNit && limpio.length >= 6) {
      this.formaForm.get('codigo')!.setValue(calcularDigitoVerificacionNIT(limpio), {
        emitEvent: false
      });
    }
    this.formaForm.updateValueAndValidity({ emitEvent: false });
  }

  private validadorDocumento(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const tipo = group.get('tipo')?.value;
      const numero = group.get('numero')?.value;
      const numCtrl = group.get('numero');
      if (!tipo || !String(numero ?? '').trim()) {
        return null;
      }
      if (numeroDocumentoInvalido(tipo, numero)) {
        numCtrl?.setErrors({ ...(numCtrl.errors || {}), sincodigo: true });
        return { sincodigo: true };
      }
      if (numCtrl?.hasError('sincodigo')) {
        const { sincodigo: _drop, ...rest } = numCtrl.errors || {};
        numCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    };
  }

  private armarNombreSaludo(nombreCompleto: unknown): string {
    const parts = String(nombreCompleto ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) {
      return '';
    }
    if (parts.length === 1) {
      return this.toTitleCase(parts[0]);
    }
    if (parts.length === 2) {
      return this.toTitleCase(`${parts[0]} ${parts[1]}`);
    }
    return this.toTitleCase(`${parts[0]} ${parts[parts.length - 2]}`);
  }

  private toTitleCase(value: string): string {
    return value.toLowerCase().replace(/(^|\s)\S/g, (ch) => ch.toUpperCase());
  }

  private coincideFiltro(item: EstadoTramiteSolicitud, query: string): boolean {
    return (
      this.normalizarTexto(item.solicitudId).includes(query) ||
      this.normalizarTexto(item.ordenPedidoId).includes(query) ||
      this.normalizarTexto(item.politica).includes(query)
    );
  }

  private campoCompartidoEnListado(
    getter: (item: EstadoTramiteSolicitud) => unknown
  ): boolean {
    const list = this.listadoSolicitudes;
    if (list.length < 1) {
      return false;
    }
    const key = this.normalizarTexto(getter(list[0]));
    if (!key) {
      return false;
    }
    return list.every((item) => this.normalizarTexto(getter(item)) === key);
  }

  private normalizarTexto(value: unknown): string {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
    const tipo = this.formaForm.get('tipo');
    const numero = this.formaForm.get('numero');
    const codigo = this.formaForm.get('codigo');
    if (loading) {
      tipo?.disable({ emitEvent: false });
      numero?.disable({ emitEvent: false });
      codigo?.disable({ emitEvent: false });
    } else {
      tipo?.enable({ emitEvent: false });
      numero?.enable({ emitEvent: false });
      if (this.esNit) {
        codigo?.enable({ emitEvent: false });
      }
    }
  }

  private extraerMensajeError(error: {
    error?: { mensaje?: string; descripcionErrores?: string[]; message?: string };
    message?: string;
  }): string {
    return (
      error?.error?.mensaje ||
      (Array.isArray(error?.error?.descripcionErrores)
        ? error.error.descripcionErrores.join('\n')
        : null) ||
      error?.error?.message ||
      error?.message ||
      'No fue posible consultar el estado del trámite.'
    );
  }
}
